import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Toaster } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, MessageSquare, Users, Settings, LogOut,
  Phone, Building2, Globe, Mail, FileText, ImageIcon, TrendingUp, Shield, RefreshCw, FileEdit, Briefcase,
  Info, Layers, Image, Compass
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

// Admin Tab Components
import DashboardTab from '../components/admin/DashboardTab';
import SubmissionsTab from '../components/admin/SubmissionsTab';
import TeamTab from '../components/admin/TeamTab';
import CompanyInfoTab from '../components/admin/CompanyInfoTab';
import ContactDetailsTab from '../components/admin/ContactDetailsTab';
import SocialLinksTab from '../components/admin/SocialLinksTab';
import PartnerLogosTab from '../components/admin/PartnerLogosTab';
import EmailSignaturesTab from '../components/admin/EmailSignaturesTab';
import EmailTemplatesTab from '../components/admin/EmailTemplatesTab';
import BusinessPlanTab from '../components/admin/BusinessPlanTab';
import ComplaintsTab from '../components/admin/ComplaintsTab';
import SettingsTab from '../components/admin/SettingsTab';
import LegalPagesTab from '../components/admin/LegalPagesTab';
import AboutSectionTab from '../components/admin/AboutSectionTab';
import ServicesSectionTab from '../components/admin/ServicesSectionTab';
import PortfolioTab from '../components/admin/PortfolioTab';
import ApproachTab from '../components/admin/ApproachTab';

const menuItems = [
  { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { id: 'submissions', icon: MessageSquare, label: 'Enquiries' },
  { id: 'crm', icon: TrendingUp, label: 'CRM', isLink: true, href: '/crm' },
  { id: 'about-section', icon: Info, label: 'About Us' },
  { id: 'services-section', icon: Layers, label: 'Services' },
  { id: 'portfolio-section', icon: Image, label: 'Portfolio' },
  { id: 'approach-section', icon: Compass, label: 'Our Approach' },
  { id: 'team', icon: Users, label: 'Team' },
  { id: 'company', icon: Building2, label: 'Company Info' },
  { id: 'contact', icon: Phone, label: 'Contact Details' },
  { id: 'social', icon: Globe, label: 'Social Links' },
  { id: 'logos', icon: ImageIcon, label: 'Partner Logos' },
  { id: 'signatures', icon: Mail, label: 'Email Signatures' },
  { id: 'templates', icon: FileEdit, label: 'Email Templates' },
  { id: 'business-plan', icon: Briefcase, label: 'Business Plan' },
  { id: 'complaints', icon: FileText, label: 'Complaints Policy' },
  { id: 'legal', icon: Shield, label: 'Legal Pages' },
  { id: 'settings', icon: Settings, label: 'Settings' },
];

const AdminPanel = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, login, logout, authAxios } = useAuth();
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setPinError(false);
    const result = await login(pinInput);
    if (!result.success) {
      setPinError(true);
      setPinInput('');
      setTimeout(() => setPinError(false), 2000);
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Loading screen
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
          <p className="text-white/50 mt-4 font-mono text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // PIN entry screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#111111] border border-white/10 p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_aureate-preview/artifacts/h6n4fhlc_Only%20Logo.png"
              alt="Aureate Residences"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="font-mono text-xl font-bold">Admin Access</h1>
            <p className="text-white/50 text-sm mt-2">Enter PIN to continue</p>
          </div>
          
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                placeholder="Enter PIN"
                maxLength={4}
                autoFocus
                disabled={loginLoading}
                data-testid="admin-pin-input"
                className={`w-full bg-[#0A0A0A] border ${
                  pinError ? 'border-red-500' : 'border-white/10'
                } px-4 py-4 font-mono text-center text-2xl tracking-[0.5em] focus:border-[#D4AF37] outline-none transition-colors disabled:opacity-50`}
              />
              {pinError && (
                <p className="text-red-400 text-sm text-center mt-2">Invalid PIN</p>
              )}
            </div>
            <button
              type="submit"
              disabled={loginLoading || pinInput.length !== 4}
              data-testid="admin-login-button"
              className="w-full bg-[#D4AF37] text-black py-3 font-mono font-bold hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loginLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Access Admin Panel'
              )}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <Link to="/" className="text-white/50 text-sm hover:text-white transition-colors">
              Back to Website
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardTab authAxios={authAxios} />;
      case 'submissions': return <SubmissionsTab authAxios={authAxios} />;
      case 'about-section': return <AboutSectionTab authAxios={authAxios} />;
      case 'services-section': return <ServicesSectionTab authAxios={authAxios} />;
      case 'portfolio-section': return <PortfolioTab authAxios={authAxios} />;
      case 'approach-section': return <ApproachTab authAxios={authAxios} />;
      case 'team': return <TeamTab authAxios={authAxios} />;
      case 'company': return <CompanyInfoTab authAxios={authAxios} />;
      case 'contact': return <ContactDetailsTab authAxios={authAxios} />;
      case 'social': return <SocialLinksTab authAxios={authAxios} />;
      case 'logos': return <PartnerLogosTab authAxios={authAxios} />;
      case 'signatures': return <EmailSignaturesTab authAxios={authAxios} />;
      case 'templates': return <EmailTemplatesTab authAxios={authAxios} />;
      case 'business-plan': return <BusinessPlanTab authAxios={authAxios} />;
      case 'complaints': return <ComplaintsTab authAxios={authAxios} />;
      case 'settings': return <SettingsTab authAxios={authAxios} />;
      case 'legal': return <LegalPagesTab authAxios={authAxios} />;
      default: return <DashboardTab authAxios={authAxios} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex">
      <Toaster position="top-right" toastOptions={{
        style: { background: '#161616', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }
      }} />
      
      {/* Sidebar */}
      <aside className="w-64 bg-[#111111] border-r border-white/5 flex flex-col fixed h-full">
        <div className="p-6 border-b border-white/5">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_aureate-preview/artifacts/h6n4fhlc_Only%20Logo.png"
              alt="Aureate Residences"
              className="h-10 w-auto"
            />
            <div>
              <span className="font-mono text-sm font-bold text-white block">AUREATE</span>
              <span className="font-mono text-xs text-[#D4AF37]">Admin Panel</span>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {menuItems.map(item => (
              <li key={item.id}>
                {item.isLink ? (
                  <Link
                    to={item.href}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded transition-all text-white/60 hover:bg-white/5 hover:text-white"
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-mono text-sm">{item.label}</span>
                    <span className="ml-auto text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded">NEW</span>
                  </Link>
                ) : (
                  <button
                    onClick={() => setActiveTab(item.id)}
                    data-testid={`nav-${item.id}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded transition-all ${
                      activeTab === item.id 
                        ? 'bg-[#D4AF37]/10 text-[#D4AF37]' 
                        : 'text-white/60 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-mono text-sm">{item.label}</span>
                  </button>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-white/5 space-y-2">
          <Link
            to="/"
            className="w-full flex items-center gap-3 px-4 py-3 text-white/60 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-mono text-sm">Back to Site</span>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-400/60 hover:text-red-400 transition-colors"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-mono text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 overflow-auto">
        <header className="bg-[#111111] border-b border-white/5 px-8 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-mono text-xl font-bold uppercase">
                {menuItems.find(m => m.id === activeTab)?.label}
              </h1>
              <p className="text-white/50 text-sm mt-1">Manage your website content</p>
            </div>
          </div>
        </header>

        <div className="p-8">
          {renderTabContent()}
        </div>
      </main>
    </div>
  );
};

export default AdminPanel;
